import React, { ReactNode, FC, useCallback } from 'react';

import AbstractErrorBoundary, { AbstractErrorBoundaryProps } from '../#AbstractErrorBoundary';
import TechnicalErrorPageMessage from '../../message/TechnicalErrorPageMessage';

export interface PageErrorBoundaryProps extends Omit<AbstractErrorBoundaryProps, 'ErrorMessage'> {
  /** The content to display when there is no error */
  children?: ReactNode;
    /** Custom error message */
  message?: ReactNode;
  /** Text of the retry button. If not defined, no error button will display */
  retryButton?: ReactNode;
  /** The title of the error */
  errorTitle?: ReactNode;
  /** Callback of the retry button */
  onRetry?: () => void;
  /** Force an error to display. */
  forceError?: boolean;
  /** @ignore */
  className?: string;
}

const PageErrorBoundary: FC<PageErrorBoundaryProps> = ({ forceError, errorTitle, children, message, retryButton, onRetry, className, ...props }) => {
  const ErrorMessage = useCallback(() => (
    <TechnicalErrorPageMessage
      errorTitle={errorTitle}
      message={message}
      buttonText={retryButton}
      onButtonClick={onRetry}
      className={className}
    />
  ), [errorTitle, message, onRetry, retryButton, className]);

  if (forceError) {
    return <ErrorMessage />;
  }

  return (
    <AbstractErrorBoundary
      data-testid="ErrorBoundary"
      {...props}
      ErrorMessage={ErrorMessage}
    >
      {children}
    </AbstractErrorBoundary>
  );
};

export default PageErrorBoundary;
